import { z } from 'zod';

import { definedNotNull } from '../defined/index.js';

declare const timestampBrand: unique symbol;

/**
 * Unix timestamp in whole microseconds — the backend wire form. Compare with
 * `<`/`>` and subtract for a delta; render through the `Time.Timestamp` namespace
 * (`display` / `displayRelative` / `timeSince`) rather than the raw number.
 */
export type Timestamp = number & { readonly [timestampBrand]: 'Timestamp' };

/**
 * The only place a `number` becomes a `Timestamp`; microseconds is the wire unit.
 *
 * Rounding here is what makes the type whole everywhere: the backend decodes a
 * timestamp as an integer and rejects the entire request body over a fractional
 * one, while sub-microsecond sources (the `performance` clock, millisecond
 * arithmetic) are fractional. A whole microsecond count is an exact `number`
 * until the year 2255, so no precision is at stake.
 */
const brand = (microseconds: number): Timestamp => Math.round(microseconds) as Timestamp;

const toDate = (timestamp: Timestamp): Date => new Date(timestamp / 1000);

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const MS_PER_DAY = 86_400_000;

/** Human-readable "time ago" in Swedish for a non-negative second count. */
function swedishTimeAgo(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  if (days > 548) {
    return `${Math.floor(seconds / 31536000)} år sedan`;
  }
  if (days >= 320) {
    return 'Ett år sedan';
  }
  if (days >= 26) {
    const months = Math.floor(seconds / 2592000);
    return months === 1 ? 'En månad sedan' : `${months} månader sedan`;
  }
  const hours = Math.floor(seconds / 3600);
  if (hours >= 22) {
    return hours <= 35 ? 'Igår' : days === 1 ? 'En dag sedan' : `${days} dagar sedan`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes >= 120) {
    return `${hours} timmar sedan`;
  }
  if (minutes >= 45) {
    return 'En timme sedan';
  }
  if (seconds >= 120) {
    return `${minutes} minuter sedan`;
  }
  if (seconds >= 45) {
    return 'En minut sedan';
  }
  return 'Alldeles nyss';
}

export const Timestamp = {
  /** The unix epoch — a neutral sentinel / default. */
  epoch: brand(0),

  now(): Timestamp {
    return brand((window.performance.timeOrigin + window.performance.now()) * 1000);
  },

  /** From unix seconds — for JWT claims and backend-shared second literals. */
  fromUnixTimeSeconds(unixTime: number): Timestamp {
    return brand(unixTime * 1000 * 1000);
  },

  /** Decodes the wire number (microseconds) into a branded `Timestamp`. */
  schema: z.number().transform(brand),

  addMilliseconds(timestamp: Timestamp, milliseconds: number): Timestamp {
    return brand(timestamp + milliseconds * 1000);
  },

  toJsDate(timestamp: Timestamp): Date {
    return toDate(timestamp);
  },

  /** Absolute local datetime, e.g. `2026-02-10 14:30:00`. */
  display(timestamp: Timestamp): string {
    return toDate(timestamp).toLocaleString('sv-SE');
  },

  /** `display(t)`, or `fallback` when the timestamp is absent. */
  displayOr(timestamp: Timestamp | null | undefined, fallback = '-'): string {
    return definedNotNull(timestamp) ? Timestamp.display(timestamp) : fallback;
  },

  /** Calendar-relative datetime: `idag 14:30`, `igår 14:30`, `mån 14:30`, else `2026-02-10 14:30`. */
  displayRelative(timestamp: Timestamp): string {
    const date = toDate(timestamp);
    const now = new Date();
    const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / MS_PER_DAY);
    const time = date.toLocaleTimeString('sv').slice(0, 5);
    if (dayDiff === 0) {
      return `idag ${time}`;
    }
    if (dayDiff === 1) {
      return `igår ${time}`;
    }
    if (date.getTime() < now.getTime() + 6 * MS_PER_DAY) {
      return `${date.toLocaleDateString('sv', { weekday: 'short' })} ${time}`;
    }
    return `${date.toLocaleDateString('sv')} ${time}`;
  },

  /** Elapsed time until now, in Swedish, e.g. `3 timmar sedan` / `Alldeles nyss`. */
  timeSince(timestamp: Timestamp): string {
    return swedishTimeAgo(Math.floor((Date.now() - timestamp / 1000) / 1000));
  },
};
